/**
 * Standardized Error Handling Utility
 * 
 * Provides consistent error handling patterns across the application.
 * 
 * ## Error Handling Philosophy
 * 
 * ### When to THROW errors:
 * - Permission/authorization violations
 * - Data validation failures
 * - Critical business logic violations
 * - Required data not found (when it should exist)
 * 
 * ### When to RETURN NULL:
 * - Optional data fetches ("not found" is valid)
 * - Public-facing queries where data may not exist
 * - Graceful degradation scenarios
 * 
 * ### When to FAIL SILENTLY:
 * - Analytics/tracking operations
 * - Activity logging
 * - Background/non-critical tasks
 * - Operations where failure shouldn't block the user
 */

/**
 * Extract error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error && typeof error === 'object') {
    // Firebase error format
    if ('code' in error && typeof (error as any).code === 'string') {
      return `Error: ${(error as any).code}`;
    }
    
    // Error with message property
    if ('message' in error && typeof (error as any).message === 'string') {
      return (error as any).message;
    }
  }
  
  return 'An unknown error occurred';
}

/**
 * Get user-friendly error message based on error code or type
 */
export function getUserFriendlyMessage(error: unknown, context?: string): string {
  const errorMsg = getErrorMessage(error);
  const prefix = context ? `${context}: ` : '';
  
  // Firebase-specific errors
  if (errorMsg.includes('permission-denied') || errorMsg.includes('insufficient permissions')) {
    return `${prefix}You don't have permission to perform this action.`;
  }
  
  if (errorMsg.includes('not-found')) {
    return `${prefix}The requested item could not be found.`;
  }
  
  if (errorMsg.includes('already-exists')) {
    return `${prefix}This item already exists.`;
  }
  
  if (errorMsg.includes('network') || errorMsg.includes('Network')) {
    return `${prefix}Network error. Please check your connection and try again.`;
  }
  
  if (errorMsg.includes('quota-exceeded') || errorMsg.includes('rate-limit')) {
    return `${prefix}Too many requests. Please try again in a moment.`;
  }
  
  // Return original message with context
  return `${prefix}${errorMsg}`;
}

/**
 * Check if error is a permission/authorization error
 */
export function isPermissionError(error: unknown): boolean {
  const errorMsg = getErrorMessage(error);
  return (
    errorMsg.includes('permission-denied') ||
    errorMsg.includes('insufficient permissions') ||
    errorMsg.includes('Unauthorized') ||
    errorMsg.includes('forbidden')
  );
}

/**
 * Check if error is a "not found" error (which may be acceptable)
 */
export function isNotFoundError(error: unknown): boolean {
  const errorMsg = getErrorMessage(error);
  return errorMsg.includes('not-found') || errorMsg.includes('not exist');
}

/**
 * Log error to console with context
 * In production, this could send to error tracking service (Sentry, etc.)
 */
export function logError(
  error: unknown,
  context: string,
  severity: 'info' | 'warning' | 'error' | 'critical' = 'error'
): void {
  const errorMsg = getErrorMessage(error);
  const timestamp = new Date().toISOString();
  
  const logMessage = `[${severity.toUpperCase()}] [${timestamp}] ${context}: ${errorMsg}`;
  
  switch (severity) {
    case 'info':
      console.info(logMessage);
      break;
    case 'warning':
      console.warn(logMessage);
      break;
    case 'critical':
      console.error(`🚨 CRITICAL ERROR: ${logMessage}`);
      // In production, send to error tracking service
      break;
    default:
      console.error(logMessage);
  }
  
  // Log full error object in development
  if (process.env.NODE_ENV === 'development' && error instanceof Error) {
    console.error('Full error object:', error);
  }
}

/**
 * Handle user-facing errors (operations initiated by user action)
 * Logs error and re-throws with user-friendly message
 * 
 * @example
 * ```typescript
 * try {
 *   await updateArticle(id, data);
 * } catch (error) {
 *   throw handleUserError(error, 'Failed to update article');
 * }
 * ```
 */
export function handleUserError(error: unknown, context: string): Error {
  logError(error, context, 'error');
  const userMessage = getUserFriendlyMessage(error, context);
  return new Error(userMessage);
}

/**
 * Handle critical errors that should never be ignored
 * Logs with CRITICAL severity and throws
 * 
 * @example
 * ```typescript
 * if (!db) {
 *   handleCriticalError(new Error('Database not initialized'), 'Service initialization');
 * }
 * ```
 */
export function handleCriticalError(error: unknown, context: string): never {
  logError(error, context, 'critical');
  const errorMsg = getErrorMessage(error);
  throw new Error(`CRITICAL: ${context} - ${errorMsg}`);
}

/**
 * Handle background/non-critical errors silently
 * Only logs, does not throw or interrupt flow
 * 
 * @example
 * ```typescript
 * try {
 *   await logActivity('article_viewed', user, 'article', articleId);
 * } catch (error) {
 *   handleSilentError(error, 'Activity logging');
 *   // Continue execution - logging failure shouldn't block user
 * }
 * ```
 */
export function handleSilentError(error: unknown, context: string): void {
  logError(error, context, 'warning');
  // Does not throw - silently logs and continues
}

/**
 * Handle data fetch errors gracefully
 * Returns null for acceptable "not found" scenarios, throws for real errors
 * 
 * @example
 * ```typescript
 * async getArticle(id: string): Promise<Article | null> {
 *   try {
 *     const doc = await getDoc(docRef);
 *     if (!doc.exists()) return null;
 *     return doc.data() as Article;
 *   } catch (error) {
 *     return handleDataFetchError(error, 'getArticle', id);
 *   }
 * }
 * ```
 */
export function handleDataFetchError<T = null>(
  error: unknown,
  operation: string,
  resourceId?: string
): T | null {
  // Permission errors during SSR or public access - return null gracefully
  if (isPermissionError(error)) {
    logError(error, `${operation} - Permission denied (graceful)${resourceId ? ` for ${resourceId}` : ''}`, 'info');
    return null;
  }
  
  // Not found is acceptable for data fetches
  if (isNotFoundError(error)) {
    logError(error, `${operation} - Not found${resourceId ? ` (${resourceId})` : ''}`, 'info');
    return null;
  }
  
  // Real errors should throw
  throw handleUserError(error, operation);
}

/**
 * Wrap async operations with standardized error handling
 * 
 * @example
 * ```typescript
 * const result = await withErrorHandling(
 *   () => updateArticle(id, data),
 *   'Update article',
 *   { throwOnError: true }
 * );
 * ```
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  context: string,
  options: {
    /** Throw error vs return null (default: true) */
    throwOnError?: boolean;
    /** Silent failure (default: false) */
    silent?: boolean;
    /** Log severity (default: 'error') */
    severity?: 'info' | 'warning' | 'error' | 'critical';
  } = {}
): Promise<T | null> {
  const { throwOnError = true, silent = false, severity = 'error' } = options;
  
  try {
    return await operation();
  } catch (error) {
    if (silent) {
      logError(error, context, 'warning');
      return null;
    }
    
    logError(error, context, severity);
    
    if (throwOnError) {
      throw handleUserError(error, context);
    }
    
    return null;
  }
}

/**
 * Validation error class for business logic validation failures
 */
export class ValidationError extends Error {
  constructor(message: string, public field?: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

/**
 * Permission error class for authorization failures
 */
export class PermissionError extends Error {
  constructor(message: string, public requiredPermission?: string) {
    super(message);
    this.name = 'PermissionError';
  }
}

/**
 * Not found error class for missing resources
 */
export class NotFoundError extends Error {
  constructor(message: string, public resourceType?: string, public resourceId?: string) {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Helper to check if user has permission, throw if not
 * 
 * @example
 * ```typescript
 * assertPermission(
 *   checkPermission(Permission.ARTICLE_PUBLISH),
 *   'publish articles'
 * );
 * ```
 */
export function assertPermission(hasPermission: boolean, action: string): void {
  if (!hasPermission) {
    throw new PermissionError(`You don't have permission to ${action}`);
  }
}

/**
 * Helper to validate required fields
 * 
 * @example
 * ```typescript
 * assertRequired(articleData.title, 'title', 'Article title is required');
 * assertRequired(articleData.content, 'content', 'Article content is required');
 * ```
 */
export function assertRequired<T>(
  value: T | null | undefined,
  fieldName: string,
  message?: string
): asserts value is T {
  if (value === null || value === undefined || value === '') {
    throw new ValidationError(message || `${fieldName} is required`, fieldName);
  }
}

/**
 * Helper to assert a resource exists
 * 
 * @example
 * ```typescript
 * const article = await getArticle(id);
 * assertExists(article, 'article', id);
 * // article is now typed as non-null
 * ```
 */
export function assertExists<T>(
  value: T | null | undefined,
  resourceType: string,
  resourceId?: string
): asserts value is T {
  if (value === null || value === undefined) {
    throw new NotFoundError(
      `${resourceType} not found${resourceId ? `: ${resourceId}` : ''}`,
      resourceType,
      resourceId
    );
  }
}

